<template>
  <LegalShell
    active="delete"
    eyebrow="Muzilly account"
    title="Delete your Muzilly account"
    subtitle="This page explains how to delete your Muzilly account and the data attached to it, what is removed straight away, and what we are legally required to keep."
  >
    <section class="lg-section" id="how">
      <h2 class="lg-h2">How to delete your account</h2>
      <p class="lg-p"><strong>In the Muzilly app</strong> (Android or iPhone):</p>
      <ol class="lg-steps">
        <li>Open Muzilly and sign in.</li>
        <li>Go to your <strong>Profile</strong> and scroll to the bottom.</li>
        <li>Tap <strong>Delete account</strong>, type <strong>DELETE</strong>, and confirm.</li>
      </ol>
      <p class="lg-p"><strong>On this page</strong> (web): sign in to Muzilly, then use the button below.</p>

      <div class="lg-callout red">
        <div class="lg-callout-title">Delete my account</div>
        <template v-if="!ready"><p class="lg-p">Checking your sign-in…</p></template>
        <template v-else-if="done">
          <p class="lg-p"><strong>Your account has been deleted.</strong> Your sign-in, profile and content have been removed. Thanks for being part of Muzilly.</p>
        </template>
        <template v-else-if="!user">
          <p class="lg-p">You're not signed in. <router-link class="lg-a" to="/">Sign in</router-link> and come back to this page to delete your account, or email <a class="lg-a" href="mailto:support@muzilly.com">support@muzilly.com</a> from your account email and we'll do it for you within 30 days.</p>
        </template>
        <template v-else>
          <p class="lg-p">Signed in as <strong>{{ user.email }}</strong>. Deleting is <strong>permanent</strong> and can't be undone. If you have a membership billed by Apple or Google Play, cancel it there first (see below). Cash out any earnings first too.</p>
          <p class="lg-p">Type <strong>DELETE</strong> to confirm:</p>
          <input v-model.trim="typed" class="dl-input" type="text" placeholder="DELETE" autocomplete="off" autocapitalize="characters" />
          <p v-if="error" class="dl-error">{{ error }}</p>
          <button class="lg-btn danger" :disabled="busy || typed !== 'DELETE'" @click="remove">{{ busy ? "Deleting…" : "Permanently delete my account" }}</button>
        </template>
      </div>
      <p class="lg-p"><strong>Can't sign in?</strong> Email <a class="lg-a" href="mailto:support@muzilly.com">support@muzilly.com</a> from the email address on your account with the subject "Delete my account". We'll verify it's you and delete the account within 30 days.</p>
    </section>

    <section class="lg-section" id="deleted">
      <h2 class="lg-h2">What is deleted</h2>
      <p class="lg-p">When you delete your account, these are removed from Muzilly:</p>
      <ul class="lg-list">
        <li>Your sign-in and profile (name, email, username, stage or band name, photo, bio, genres, location)</li>
        <li>Your tracks, albums, cover art and uploaded audio files</li>
        <li>Your playlists, likes, comments, follows (people you follow and your followers) and listening history</li>
        <li>Direct messages you sent or received</li>
        <li>Your collaboration calls and your notifications</li>
        <li>Your blocked-users list, and your membership record</li>
        <li>A web (Stripe) membership is cancelled so you aren't billed again</li>
      </ul>
    </section>

    <section class="lg-section" id="kept">
      <h2 class="lg-h2">What we keep, and for how long</h2>
      <ul class="lg-list">
        <li><strong>Payment and payout records</strong> (membership payments, coin purchases and gifts, earnings and cash-outs, tax records). The law requires us to keep financial records, typically up to 7 years. They're used only for accounting, tax and legal purposes.</li>
        <li><strong>Shared records involving other people</strong>: live-room chat and gifts you sent, co-signs, and collaboration projects with split sheets, so your collaborators' projects and earnings aren't broken. Your name may remain as a credit.</li>
        <li><strong>Safety records</strong>: reports about you or filed by you, and suspension and moderation records, kept as long as needed to protect other users.</li>
        <li><strong>Backups and logs</strong> held by our providers are overwritten on their normal schedules.</li>
      </ul>
    </section>

    <section class="lg-section" id="subs">
      <h2 class="lg-h2">Before you delete: subscriptions and earnings</h2>
      <div class="lg-items">
        <div class="lg-item"><span class="lg-dot"></span><div><div class="lg-item-title">Google Play membership</div><div class="lg-item-text">Deleting your account does <strong>not</strong> cancel a Google Play subscription. Cancel it in Google Play → Payments &amp; subscriptions → Subscriptions → Muzilly.</div></div></div>
        <div class="lg-item"><span class="lg-dot teal"></span><div><div class="lg-item-title">Apple membership</div><div class="lg-item-text">Cancel in iPhone Settings → your name → Subscriptions → Muzilly.</div></div></div>
        <div class="lg-item"><span class="lg-dot"></span><div><div class="lg-item-title">Earnings</div><div class="lg-item-text">Artists: cash out any earnings (minimum $10) from your earnings screen first. Once the account is deleted you can't sign in to claim them. If something is left unpaid, email support@muzilly.com within 30 days.</div></div></div>
        <div class="lg-item"><span class="lg-dot teal"></span><div><div class="lg-item-title">Admin accounts</div><div class="lg-item-text">Muzilly staff accounts can't be deleted from here.</div></div></div>
      </div>
    </section>

    <div class="lg-cta">
      <h3 class="lg-cta-title">Rather keep your account?</h3>
      <p class="lg-cta-text">You can change your details, mute notifications or cancel your membership without deleting anything.</p>
      <router-link class="lg-btn ghost" to="/">Back to Muzilly</router-link>
    </div>
  </LegalShell>
</template>

<script>
import LegalShell from "@/components/LegalShell.vue";
import { auth, functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";

export default {
  name: "DeleteAccountView",
  components: { LegalShell },
  data() {
    return { user: null, ready: false, typed: "", busy: false, error: "", done: false, unsub: null };
  },
  mounted() {
    this.unsub = auth.onAuthStateChanged((u) => { this.user = u; this.ready = true; });
  },
  beforeDestroy() {
    if (this.unsub) this.unsub();
  },
  methods: {
    async remove() {
      if (this.typed !== "DELETE" || this.busy) return;
      this.busy = true; this.error = "";
      try {
        await httpsCallable(functions, "deleteMyAccount")();
        this.done = true;
        try { await auth.signOut(); } catch (e) { /* the account is already gone */ }
      } catch (e) {
        this.error = (e && e.message) || "Something went wrong. Please try again or email support@muzilly.com.";
      }
      this.busy = false;
    },
  },
};
</script>

<style scoped>
.dl-input{width:100%;max-width:260px;padding:11px 12px;margin:2px 0 14px;display:block;background:rgba(245,240,232,0.05);border:0.5px solid rgba(245,240,232,0.2);border-radius:9px;color:#f5f0e8;font-size:14px;font-family:inherit}
.dl-input:focus{outline:none;border-color:#d48c1c}
.dl-error{color:#e08b84;font-size:13px;margin:0 0 12px}
</style>